import { useState, useRef, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { analytics } from "./firebase";
import { styled } from "@mui/material/styles";
import {
    Box,
    Button,
    CssBaseline,
    Grid,
    Toolbar,
    AppBar,
    Typography,
    Snackbar,
    useMediaQuery,
    IconButton,
    Chip,
} from "@mui/material";
import {
    CloudDownload,
    Google,
    ContentCopy,
    DarkMode,
    LightMode,
} from "@mui/icons-material";
import Editor from "./components/Editor";
import Preview from "./components/Preview";
import Auth from "./components/Auth";
import SEO from "./seo";
import html2pdf from "html2pdf.js";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    BorderStyle,
    WidthType,
    LevelFormat,
    AlignmentType,
} from "docx";
import { saveAs } from "file-saver";
import { logEvent } from "firebase/analytics";

const Root = styled("div")(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        gap: theme.spacing(1),
    },
}));

const LeftSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
}));

const RightSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
        flexWrap: "wrap",
        justifyContent: "center",
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: "10px",
    padding: theme.spacing(0.8, 2),
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.83rem",
    boxShadow: "none",
    transition: "all 0.2s ease",
    letterSpacing: "0.01em",
    color: "#ffffff",
    "&:hover": {
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: "none",
    },
    "& .MuiButton-startIcon": {
        marginRight: theme.spacing(0.75),
        "& .MuiSvgIcon-root": {
            fontSize: "16px",
        },
    },
}));

const ContentContainer = styled(Grid)(({ theme }) => ({
    height: "calc(100vh - 64px)",
    [theme.breakpoints.down("md")]: {
        height: "auto",
    },
}));

const EditorContainer = styled(Grid)(({ theme }) => ({
    height: "100%",
    [theme.breakpoints.down("md")]: {
        height: "50vh",
    },
}));

const PreviewContainer = styled(Grid)(({ theme }) => ({
    height: "100%",
    [theme.breakpoints.down("md")]: {
        height: "50vh",
    },
}));

const SEOSection = styled("section")(({ theme }) => ({
    padding: "64px 24px",
    maxWidth: "960px",
    margin: "0 auto",
    color: theme.palette.mode === "dark" ? "#cbd5e1" : "#475569",
    "& h2": {
        fontSize: "1.75rem",
        fontWeight: 700,
        marginBottom: "1rem",
        color: theme.palette.mode === "dark" ? "#e2e8f0" : "#1e293b",
    },
    "& h3": {
        fontSize: "1.25rem",
        fontWeight: 600,
        marginBottom: "0.5rem",
        marginTop: "1.5rem",
        color: theme.palette.mode === "dark" ? "#e2e8f0" : "#334155",
    },
    "& p": {
        lineHeight: 1.7,
        marginBottom: "1rem",
    },
    "& ul": {
        paddingLeft: "1.5rem",
        marginBottom: "1rem",
    },
    "& li": {
        marginBottom: "0.5rem",
        lineHeight: 1.6,
    },
}));

const FAQItem = styled("details")(({ theme }) => ({
    marginBottom: "12px",
    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
    borderRadius: "8px",
    overflow: "hidden",
    "& summary": {
        padding: "16px 20px",
        fontWeight: 600,
        cursor: "pointer",
        backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
        color: theme.palette.mode === "dark" ? "#e2e8f0" : "#1e293b",
        fontSize: "1rem",
        lineHeight: 1.4,
        "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#f1f5f9",
        },
        "&::marker": {
            color: "#3b82f6",
        },
    },
    "& p": {
        padding: "12px 20px 16px",
        margin: 0,
        lineHeight: 1.7,
    },
}));

const DEFAULT_MARKDOWN = `# Welcome to Markdown Converter Pro

Convert your Markdown to **PDF**, **Word**, or **Google Docs** with one click.

## Getting Started

Use the **toolbar above** to format your text, or write Markdown directly:

### Text Formatting
- **Bold text** with \`**bold**\`
- *Italic text* with \`*italic*\`
- ~~Strikethrough~~ with \`~~text~~\`
- \`Inline code\` with backticks

### Lists
1. First ordered item
2. Second ordered item
3. Third ordered item

- Unordered item
- Another item
  - Nested item

### Task List
- [x] Write markdown content
- [x] Preview in real-time
- [ ] Export to your preferred format

### Code Block
\`\`\`javascript
function convert(markdown) {
  return exportAs(markdown, "pdf");
}
\`\`\`

### Table
| Format | Quality | Speed |
| ------ | ------- | ----- |
| PDF    | High    | Fast  |
| DOCX   | High    | Fast  |
| Google | High    | Fast  |

> **Tip:** Click **Copy** to paste rich-formatted text into any app!

---

Ready to export? Use the buttons in the toolbar above.
`;

export default function App() {
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem("themeMode");
        return savedMode ? savedMode : "light";
    });
    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    ...(mode === "dark"
                        ? {
                              background: {
                                  default: "#0f172a",
                                  paper: "#1e293b",
                              },
                              text: {
                                  primary: "#e2e8f0",
                                  secondary: "#94a3b8",
                              },
                          }
                        : {
                              background: {
                                  default: "#f8fafc",
                                  paper: "#ffffff",
                              },
                          }),
                    primary: {
                        main: "#0ea5e9",
                        light: "#38bdf8",
                        dark: "#0284c7",
                    },
                },
                shape: { borderRadius: 10 },
                typography: {
                    fontFamily:
                        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                },
            }),
        [mode]
    );
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [markdown, setMarkdown] = useState(DEFAULT_MARKDOWN);
    const [authOpen, setAuthOpen] = useState(false);
    const [loading, setLoading] = useState({
        pdf: false,
        docx: false,
        google: false,
    });
    const [copySuccess, setCopySuccess] = useState(false);
    const [googleToken, setGoogleToken] = useState(null);
    const previewRef = useRef(null);

    const wordCount = useMemo(() => {
        const text = markdown.trim();
        if (!text) return { words: 0, chars: 0 };
        return {
            words: text.split(/\s+/).filter(Boolean).length,
            chars: text.length,
        };
    }, [markdown]);

    const toggleDarkMode = () => {
        const newMode = mode === "dark" ? "light" : "dark";
        setMode(newMode);
        localStorage.setItem("themeMode", newMode);
        logEvent(analytics, "toggled_theme_mode", { mode: newMode });
    };

    const handleCopy = async () => {
        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            logEvent(analytics, "clicked_copy_to_clipboard", {});

            const htmlContent = previewRef.current.innerHTML;

            const clipboardItem = new ClipboardItem({
                "text/html": new Blob([htmlContent], { type: "text/html" }),
                "text/plain": new Blob([previewRef.current.innerText], {
                    type: "text/plain",
                }),
            });

            await navigator.clipboard.write([clipboardItem]);
            setCopySuccess(true);
            logEvent(analytics, "copied_rich_text", {});
        } catch (err) {
            try {
                const tempElem = document.createElement("div");
                tempElem.innerHTML = previewRef.current.innerHTML;

                const cleanHtml = tempElem.innerHTML;

                const textarea = document.createElement("textarea");
                textarea.innerHTML = cleanHtml;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);

                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);

                setCopySuccess(true);
                logEvent(analytics, "copied_rich_text_fallback", {});
            } catch (fallbackErr) {
                console.error("Failed to copy text (fallback): ", fallbackErr);
                logEvent(analytics, "failed_copy", {
                    error: fallbackErr.message,
                });
            }
        }
    };

    const handleSnackbarClose = () => {
        setCopySuccess(false);
    };

    const generatePdf = async () => {
        setLoading((prev) => ({ ...prev, pdf: true }));
        logEvent(analytics, "clicked_pdf", {});
        const element = previewRef.current;

        const opt = {
            margin: [10, 10, 10, 10],
            filename: "document.pdf",
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
                scale: 2,
                useCORS: true,
            },
            jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "portrait",
            },
        };

        html2pdf().set(opt).from(element).save();
        setLoading((prev) => ({ ...prev, pdf: false }));
    };

    const generateDocx = async () => {
        setLoading((prev) => ({ ...prev, docx: true }));
        logEvent(analytics, "clicked_word", {});

        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            const doc = new Document({
                numbering: {
                    config: [
                        {
                            reference: "bullet-list",
                            levels: [
                                {
                                    level: 0,
                                    format: LevelFormat.BULLET,
                                    text: "\u2022",
                                    alignment: AlignmentType.LEFT,
                                    style: {
                                        paragraph: {
                                            indent: {
                                                left: 720,
                                                hanging: 360,
                                            },
                                        },
                                    },
                                },
                            ],
                        },
                    ],
                },
                sections: [
                    {
                        properties: {},
                        children: convertHTMLToDocxContent(
                            previewRef.current.innerHTML
                        ),
                    },
                ],
            });

            const blob = await Packer.toBlob(doc);
            saveAs(blob, "document.docx");
        } catch (error) {
            console.error("DOCX generation error:", error);
            alert("Failed to generate Word document. Please try again.");
            logEvent(analytics, "failed_word", { error: error.message });
        } finally {
            setLoading((prev) => ({ ...prev, docx: false }));
        }
    };

    // Extract inline TextRuns from an element, preserving bold/italic/code/strike formatting
    const extractInlineRuns = (node, inheritedStyle = {}) => {
        const runs = [];
        Array.from(node.childNodes).forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent;
                if (text) {
                    const runOpts = { text };
                    if (inheritedStyle.bold) runOpts.bold = true;
                    if (inheritedStyle.italics) runOpts.italics = true;
                    if (inheritedStyle.strike) runOpts.strike = true;
                    if (inheritedStyle.font) runOpts.font = inheritedStyle.font;
                    if (inheritedStyle.color) {
                        runOpts.color = inheritedStyle.color;
                        runOpts.underline = { type: "single" };
                    }
                    runs.push(new TextRun(runOpts));
                }
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = child.tagName.toLowerCase();
                const childStyle = { ...inheritedStyle };
                if (tag === "strong" || tag === "b") childStyle.bold = true;
                if (tag === "em" || tag === "i") childStyle.italics = true;
                if (tag === "del" || tag === "s") childStyle.strike = true;
                if (tag === "code") childStyle.font = "Courier New";
                if (tag === "a") childStyle.color = "0000FF";
                // Skip checkbox inputs, just add a marker
                if (tag === "input") {
                    const checked = child.hasAttribute("checked");
                    runs.push(new TextRun({ text: checked ? "[x] " : "[ ] " }));
                    return;
                }
                runs.push(...extractInlineRuns(child, childStyle));
            }
        });
        return runs;
    };

    const convertHTMLToDocxContent = (html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        const docxChildren = [];

        const headingMap = {
            h1: HeadingLevel.HEADING_1,
            h2: HeadingLevel.HEADING_2,
            h3: HeadingLevel.HEADING_3,
            h4: HeadingLevel.HEADING_4,
            h5: HeadingLevel.HEADING_5,
            h6: HeadingLevel.HEADING_6,
        };

        const processBlock = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent.trim();
                if (text) {
                    docxChildren.push(
                        new Paragraph({ children: [new TextRun(text)] })
                    );
                }
                return;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) return;

            const tag = node.tagName.toLowerCase();

            // Headings
            if (headingMap[tag]) {
                docxChildren.push(
                    new Paragraph({
                        heading: headingMap[tag],
                        children: extractInlineRuns(node, { bold: true }),
                    })
                );
                return;
            }

            switch (tag) {
                case "p":
                    docxChildren.push(
                        new Paragraph({
                            children: extractInlineRuns(node),
                        })
                    );
                    break;

                case "ul":
                case "ol":
                    Array.from(node.children).forEach((li, idx) => {
                        const runs = extractInlineRuns(li);
                        if (tag === "ol") {
                            // Numbered list: prefix with number
                            runs.unshift(new TextRun({ text: `${idx + 1}. ` }));
                            docxChildren.push(
                                new Paragraph({ children: runs })
                            );
                        } else {
                            docxChildren.push(
                                new Paragraph({
                                    numbering: {
                                        reference: "bullet-list",
                                        level: 0,
                                    },
                                    children: runs,
                                })
                            );
                        }
                    });
                    break;

                case "blockquote":
                    docxChildren.push(
                        new Paragraph({
                            border: {
                                left: {
                                    color: "808080",
                                    space: 4,
                                    style: BorderStyle.SINGLE,
                                    size: 4,
                                },
                            },
                            children: extractInlineRuns(node, {
                                italics: true,
                            }),
                        })
                    );
                    break;

                case "pre":
                    docxChildren.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: node.textContent,
                                    font: "Courier New",
                                }),
                            ],
                        })
                    );
                    break;

                case "table": {
                    const rows = Array.from(node.querySelectorAll("tr")).map(
                        (tr) =>
                            new TableRow({
                                children: Array.from(
                                    tr.querySelectorAll("td, th")
                                ).map(
                                    (cell) =>
                                        new TableCell({
                                            children: [
                                                new Paragraph({
                                                    children: [
                                                        new TextRun({
                                                            text: cell.textContent,
                                                            bold:
                                                                cell.tagName.toLowerCase() ===
                                                                "th",
                                                        }),
                                                    ],
                                                }),
                                            ],
                                        })
                                ),
                            })
                    );
                    if (rows.length > 0) {
                        docxChildren.push(
                            new Table({
                                rows,
                                width: {
                                    size: 100,
                                    type: WidthType.PERCENTAGE,
                                },
                            })
                        );
                    }
                    break;
                }

                case "hr":
                    docxChildren.push(
                        new Paragraph({
                            border: {
                                bottom: {
                                    color: "999999",
                                    style: BorderStyle.SINGLE,
                                    size: 1,
                                    space: 1,
                                },
                            },
                            children: [new TextRun("")],
                        })
                    );
                    break;

                default:
                    // For unknown block elements (div, section, etc.), recurse into children
                    Array.from(node.childNodes).forEach(processBlock);
                    break;
            }
        };

        Array.from(tempDiv.childNodes).forEach(processBlock);

        return docxChildren;
    };

    // Single-pass Google Docs converter.
    // Builds insert/structural requests in order (tracking idx),
    // then appends deferred style requests at the end.
    const parseContentForGoogleDocs = (element) => {
        const requests = [];
        const styleRequests = [];
        const pendingTables = [];
        let idx = 1; // Google Docs body starts at index 1

        // Track where to split requests at first table boundary
        let tableSplitReq = -1;
        let tableSplitStyle = -1;
        let estimatedPostTableIdx = null;

        // --- helpers ---

        const pushInsert = (text, styles = {}) => {
            if (!text) return;
            const start = idx;
            requests.push({ insertText: { text, location: { index: idx } } });
            idx += text.length;
            if (!text.trim()) return;
            const end = idx;
            if (styles.bold)
                styleRequests.push({ updateTextStyle: { textStyle: { bold: true }, fields: "bold", range: { startIndex: start, endIndex: end } } });
            if (styles.italic)
                styleRequests.push({ updateTextStyle: { textStyle: { italic: true }, fields: "italic", range: { startIndex: start, endIndex: end } } });
            if (styles.strikethrough)
                styleRequests.push({ updateTextStyle: { textStyle: { strikethrough: true }, fields: "strikethrough", range: { startIndex: start, endIndex: end } } });
            if (styles.code)
                styleRequests.push({ updateTextStyle: { textStyle: { weightedFontFamily: { fontFamily: "Courier New" } }, fields: "weightedFontFamily", range: { startIndex: start, endIndex: end } } });
            if (styles.link)
                styleRequests.push({ updateTextStyle: { textStyle: { link: { url: styles.link } }, fields: "link", range: { startIndex: start, endIndex: end } } });
        };

        const setHeading = (level, start, end) => {
            styleRequests.push({
                updateParagraphStyle: {
                    paragraphStyle: { namedStyleType: `HEADING_${level}` },
                    fields: "namedStyleType",
                    range: { startIndex: start, endIndex: end },
                },
            });
        };

        const setIndent = (points, start, end) => {
            styleRequests.push({
                updateParagraphStyle: {
                    paragraphStyle: {
                        indentStart: { magnitude: points, unit: "PT" },
                        indentFirstLine: { magnitude: points, unit: "PT" },
                    },
                    fields: "indentStart,indentFirstLine",
                    range: { startIndex: start, endIndex: end },
                },
            });
        };

        // --- inline walker ---

        const insertInline = (node, inherited = {}) => {
            Array.from(node.childNodes).forEach((child) => {
                if (child.nodeType === Node.TEXT_NODE) {
                    pushInsert(child.textContent, inherited);
                } else if (child.nodeType === Node.ELEMENT_NODE) {
                    const tag = child.tagName.toLowerCase();
                    if (tag === "ul" || tag === "ol") return;
                    const styles = { ...inherited };
                    if (tag === "strong" || tag === "b") styles.bold = true;
                    if (tag === "em" || tag === "i") styles.italic = true;
                    if (tag === "del" || tag === "s") styles.strikethrough = true;
                    if (tag === "code") styles.code = true;
                    if (tag === "a") styles.link = child.href;
                    if (tag === "input") {
                        const checked = child.checked || child.hasAttribute("checked");
                        pushInsert(checked ? "[x] " : "[ ] ", inherited);
                        return;
                    }
                    insertInline(child, styles);
                }
            });
        };

        // --- list walker ---

        const processList = (listNode, depth) => {
            const listTag = listNode.tagName.toLowerCase();
            Array.from(listNode.children).forEach((li, i) => {
                const prefix = listTag === "ol" ? `${i + 1}. ` : "\u2022 ";
                const lineStart = idx;
                pushInsert(prefix);
                insertInline(li);
                pushInsert("\n");
                const lineEnd = idx;
                if (depth > 0) setIndent(depth * 36, lineStart, lineEnd);
                Array.from(li.childNodes).forEach((child) => {
                    if (child.nodeType === Node.ELEMENT_NODE) {
                        const ct = child.tagName.toLowerCase();
                        if (ct === "ul" || ct === "ol") processList(child, depth + 1);
                    }
                });
            });
        };

        // --- block walker ---

        const processBlock = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent.trim();
                if (text) pushInsert(text + "\n");
                return;
            }
            if (node.nodeType !== Node.ELEMENT_NODE) return;
            const tag = node.tagName.toLowerCase();

            if (/^h[1-6]$/.test(tag)) {
                const start = idx;
                insertInline(node);
                pushInsert("\n");
                setHeading(tag.charAt(1), start, idx);
                return;
            }

            switch (tag) {
                case "p":
                    insertInline(node);
                    pushInsert("\n");
                    break;

                case "ul":
                case "ol":
                    processList(node, 0);
                    break;

                case "blockquote":
                    insertInline(node, { italic: true });
                    pushInsert("\n");
                    break;

                case "pre":
                    pushInsert(node.textContent + "\n", { code: true });
                    break;

                case "table": {
                    const allRows = Array.from(node.querySelectorAll("tr")).map(
                        (tr) =>
                            Array.from(tr.querySelectorAll("td, th")).map(
                                (cell) => cell.textContent.trim()
                            )
                    );
                    const R = allRows.length;
                    const C = R > 0 ? Math.max(...allRows.map((r) => r.length)) : 0;
                    if (R === 0 || C === 0) break;
                    allRows.forEach((row) => { while (row.length < C) row.push(""); });

                    requests.push({
                        insertTable: { rows: R, columns: C, location: { index: idx } },
                    });
                    pendingTables.push({ data: allRows });
                    // Use a placeholder idx advance — the exact size doesn't matter
                    // because post-table requests will be adjusted after reading the doc
                    idx += 1 + R + 2 * R * C;
                    estimatedPostTableIdx = idx;
                    tableSplitReq = requests.length;
                    tableSplitStyle = styleRequests.length;
                    break;
                }

                case "hr":
                    pushInsert("────────────────────────\n");
                    break;

                default:
                    Array.from(node.childNodes).forEach(processBlock);
                    break;
            }
        };

        Array.from(element.childNodes).forEach(processBlock);

        const hasTable = tableSplitReq >= 0;
        return {
            preRequests: hasTable
                ? [...requests.slice(0, tableSplitReq), ...styleRequests.slice(0, tableSplitStyle)]
                : [...requests, ...styleRequests],
            postRequests: hasTable
                ? [...requests.slice(tableSplitReq), ...styleRequests.slice(tableSplitStyle)]
                : [],
            pendingTables,
            estimatedPostTableIdx,
        };
    };

    const handleGoogleDocs = async (accessToken) => {
        const token = googleToken || accessToken || null;
        logEvent(analytics, "clicked_google", {});
        if (!token) return setAuthOpen(true);

        setLoading((prev) => ({ ...prev, google: true }));
        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            const createDocResponse = await fetch(
                "https://docs.googleapis.com/v1/documents",
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        title: `Markdown Converter Document - ${new Date().toLocaleString()}`,
                    }),
                }
            );

            if (!createDocResponse.ok) {
                const errBody = await createDocResponse.json().catch(() => ({}));
                console.error("Create doc failed:", createDocResponse.status, errBody);
                throw new Error(
                    `Failed to create document (${createDocResponse.status}): ${errBody?.error?.message || createDocResponse.statusText}`
                );
            }

            const { documentId } = await createDocResponse.json();

            const { preRequests, postRequests, pendingTables, estimatedPostTableIdx } =
                parseContentForGoogleDocs(previewRef.current);

            // First batchUpdate: pre-table content + empty table structure
            const batchUpdateResponse = await fetch(
                `https://docs.googleapis.com/v1/documents/${documentId}:batchUpdate`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ requests: preRequests }),
                }
            );

            if (!batchUpdateResponse.ok) {
                const errBody = await batchUpdateResponse.json().catch(() => ({}));
                console.error("batchUpdate failed:", batchUpdateResponse.status, errBody);
                throw new Error(
                    `Failed to update document (${batchUpdateResponse.status}): ${errBody?.error?.message || batchUpdateResponse.statusText}`
                );
            }

            // If there are tables, read the doc to get real indices, then send post-table content + cell fills
            if (pendingTables.length > 0 && (postRequests.length > 0 || pendingTables.length > 0)) {
                const docResponse = await fetch(
                    `https://docs.googleapis.com/v1/documents/${documentId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!docResponse.ok) throw new Error("Failed to read document");
                const doc = await docResponse.json();
                const bodyContent = doc.body.content || [];

                // Find actual post-table insertion point
                const tableElIdx = bodyContent.findIndex((el) => el.table);
                const afterTableEl = bodyContent[tableElIdx + 1];
                const actualPostTableIdx = afterTableEl ? afterTableEl.startIndex : doc.body.content[doc.body.content.length - 1].endIndex - 1;
                const offset = actualPostTableIdx - estimatedPostTableIdx;

                // Adjust all post-table requests by the offset
                const adjustedPostRequests = postRequests.map((req) => {
                    const r = JSON.parse(JSON.stringify(req));
                    if (r.insertText) {
                        r.insertText.location.index += offset;
                    } else if (r.updateTextStyle) {
                        r.updateTextStyle.range.startIndex += offset;
                        r.updateTextStyle.range.endIndex += offset;
                    } else if (r.updateParagraphStyle) {
                        r.updateParagraphStyle.range.startIndex += offset;
                        r.updateParagraphStyle.range.endIndex += offset;
                    }
                    return r;
                });

                // Build table cell filling requests (reverse order — actual indices from doc)
                const docTables = bodyContent.filter((el) => el.table);
                const cellRequests = [];
                docTables.forEach((tableEl, ti) => {
                    const tableInfo = pendingTables[ti];
                    if (!tableInfo) return;
                    const rows = tableEl.table.tableRows;
                    for (let r = rows.length - 1; r >= 0; r--) {
                        const cells = rows[r].tableCells;
                        for (let c = cells.length - 1; c >= 0; c--) {
                            const text = tableInfo.data[r]?.[c] || "";
                            if (!text) continue;
                            const insertIdx = cells[c].content[0].startIndex;
                            cellRequests.push({
                                insertText: { text, location: { index: insertIdx } },
                            });
                            if (r === 0) {
                                cellRequests.push({
                                    updateTextStyle: {
                                        textStyle: { bold: true },
                                        fields: "bold",
                                        range: { startIndex: insertIdx, endIndex: insertIdx + text.length },
                                    },
                                });
                            }
                        }
                    }
                });

                // Second batchUpdate: post-table content first, then cell fills
                // Post-table goes first so cell fills (at lower indices) don't shift post-table positions
                const secondRequests = [...adjustedPostRequests, ...cellRequests];
                if (secondRequests.length > 0) {
                    const resp2 = await fetch(
                        `https://docs.googleapis.com/v1/documents/${documentId}:batchUpdate`,
                        {
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ requests: secondRequests }),
                        }
                    );
                    if (!resp2.ok) {
                        const errBody = await resp2.json().catch(() => ({}));
                        console.error("Second batchUpdate failed:", resp2.status, errBody);
                        throw new Error(
                            `Failed to complete document (${resp2.status}): ${errBody?.error?.message || resp2.statusText}`
                        );
                    }
                }
            }

            window.open(
                `https://docs.google.com/document/d/${documentId}/edit`,
                "_blank"
            );
        } catch (error) {
            console.error("Google Docs error:", error);
            alert(`Google Docs error: ${error.message}`);
            logEvent(analytics, "failed_google", { error: error.message });
        } finally {
            setLoading((prev) => ({ ...prev, google: false }));
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Root>
                <SEO />
                <CssBaseline />
                <header>
                    <AppBar
                        position="static"
                        color="default"
                        elevation={0}
                        component="nav"
                        sx={{
                            background:
                                theme.palette.mode === "dark"
                                    ? "rgba(15, 23, 42, 0.92)"
                                    : "rgba(255, 255, 255, 0.92)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            borderBottom: `1px solid ${
                                theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.06)"
                                    : "rgba(0,0,0,0.07)"
                            }`,
                            boxShadow:
                                theme.palette.mode === "dark"
                                    ? "0 4px 24px rgba(0,0,0,0.4)"
                                    : "0 1px 12px rgba(0,0,0,0.07)",
                        }}
                    >
                        <StyledToolbar>
                            <LeftSection>
                                <Typography
                                    variant={isMobile ? "h6" : "h5"}
                                    component="h1"
                                    sx={{
                                        fontWeight: 700,
                                        fontFamily:
                                            '"SF Mono", "Fira Code", Menlo, monospace',
                                        color:
                                            theme.palette.mode === "dark"
                                                ? "#f1f5f9"
                                                : "#0f172a",
                                        letterSpacing: "-0.02em",
                                    }}
                                >
                                    # Markdown Converter
                                </Typography>
                                <Chip
                                    label={`${wordCount.words}w · ${wordCount.chars}c`}
                                    size="small"
                                    sx={{
                                        fontSize: "0.7rem",
                                        height: "22px",
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "rgba(14, 165, 233, 0.12)"
                                                : "rgba(14, 165, 233, 0.08)",
                                        border: `1px solid ${
                                            theme.palette.mode === "dark"
                                                ? "rgba(14, 165, 233, 0.3)"
                                                : "rgba(14, 165, 233, 0.22)"
                                        }`,
                                        color:
                                            theme.palette.mode === "dark"
                                                ? "#38bdf8"
                                                : "#0284c7",
                                        fontWeight: 600,
                                    }}
                                />
                            </LeftSection>
                            <RightSection>
                                <IconButton
                                    onClick={toggleDarkMode}
                                    aria-label="Toggle dark mode"
                                    sx={{
                                        color:
                                            theme.palette.mode === "dark"
                                                ? "#cbd5e1"
                                                : "#475569",
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.06)"
                                                : "rgba(0,0,0,0.04)",
                                        borderRadius: "10px",
                                        padding: "7px",
                                        border: `1px solid ${
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.08)"
                                                : "rgba(0,0,0,0.08)"
                                        }`,
                                        "&:hover": {
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.12)"
                                                    : "rgba(0,0,0,0.08)",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? "#e2e8f0"
                                                    : "#1e293b",
                                        },
                                    }}
                                >
                                    {theme.palette.mode === "dark" ? (
                                        <LightMode sx={{ fontSize: "18px" }} />
                                    ) : (
                                        <DarkMode sx={{ fontSize: "18px" }} />
                                    )}
                                </IconButton>
                                <StyledButton
                                    variant="contained"
                                    startIcon={<ContentCopy />}
                                    onClick={handleCopy}
                                    aria-label="Copy as rich text"
                                    sx={{
                                        background:
                                            "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                                            boxShadow:
                                                "0 4px 18px rgba(99, 102, 241, 0.45)",
                                        },
                                    }}
                                >
                                    Copy
                                </StyledButton>
                                <StyledButton
                                    variant="contained"
                                    startIcon={<CloudDownload />}
                                    onClick={generatePdf}
                                    disabled={loading.pdf}
                                    aria-label="Export as PDF"
                                    sx={{
                                        background:
                                            "linear-gradient(135deg, #10b981 0%, #0ea5e9 100%)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg, #059669 0%, #0284c7 100%)",
                                            boxShadow:
                                                "0 4px 18px rgba(16, 185, 129, 0.45)",
                                        },
                                        "&.Mui-disabled": {
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.08)"
                                                    : "rgba(0,0,0,0.08)",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.3)"
                                                    : "rgba(0,0,0,0.3)",
                                        },
                                    }}
                                >
                                    {loading.pdf ? "Generating..." : "PDF"}
                                </StyledButton>
                                <StyledButton
                                    variant="contained"
                                    startIcon={<CloudDownload />}
                                    onClick={generateDocx}
                                    disabled={loading.docx}
                                    aria-label="Export as Word DOCX"
                                    sx={{
                                        background:
                                            "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)",
                                            boxShadow:
                                                "0 4px 18px rgba(139, 92, 246, 0.45)",
                                        },
                                        "&.Mui-disabled": {
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.08)"
                                                    : "rgba(0,0,0,0.08)",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.3)"
                                                    : "rgba(0,0,0,0.3)",
                                        },
                                    }}
                                >
                                    {loading.docx ? "Generating..." : "Word"}
                                </StyledButton>
                                <StyledButton
                                    variant="contained"
                                    startIcon={<Google />}
                                    onClick={() => handleGoogleDocs()}
                                    disabled={loading.google}
                                    aria-label="Export to Google Docs"
                                    sx={{
                                        background:
                                            "linear-gradient(135deg, #f43f5e 0%, #fb923c 100%)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg, #e11d48 0%, #ea580c 100%)",
                                            boxShadow:
                                                "0 4px 18px rgba(244, 63, 94, 0.45)",
                                        },
                                        "&.Mui-disabled": {
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.08)"
                                                    : "rgba(0,0,0,0.08)",
                                            color:
                                                theme.palette.mode === "dark"
                                                    ? "rgba(255,255,255,0.3)"
                                                    : "rgba(0,0,0,0.3)",
                                        },
                                    }}
                                >
                                    {loading.google
                                        ? "Processing..."
                                        : "Google Docs"}
                                </StyledButton>
                            </RightSection>
                        </StyledToolbar>
                    </AppBar>
                </header>
                <main>
                    <ContentContainer container>
                        <EditorContainer item xs={12} md={6}>
                            <Box
                                sx={{
                                    height: "100%",
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#0f172a"
                                            : "#f8fafc",
                                    borderRight: `1px solid ${
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.06)"
                                            : "rgba(0,0,0,0.07)"
                                    }`,
                                    "& ::-webkit-scrollbar": { width: "6px" },
                                    "& ::-webkit-scrollbar-track": {
                                        bgcolor: "transparent",
                                    },
                                    "& ::-webkit-scrollbar-thumb": {
                                        bgcolor:
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.12)"
                                                : "rgba(0,0,0,0.15)",
                                        borderRadius: "3px",
                                    },
                                }}
                            >
                                <Editor
                                    value={markdown}
                                    onChange={setMarkdown}
                                />
                            </Box>
                        </EditorContainer>
                        <PreviewContainer item xs={12} md={6}>
                            <Box
                                sx={{
                                    height: "100%",
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#1e293b"
                                            : "#ffffff",
                                    "& ::-webkit-scrollbar": { width: "6px" },
                                    "& ::-webkit-scrollbar-track": {
                                        bgcolor: "transparent",
                                    },
                                    "& ::-webkit-scrollbar-thumb": {
                                        bgcolor:
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.12)"
                                                : "rgba(0,0,0,0.15)",
                                        borderRadius: "3px",
                                    },
                                }}
                            >
                                <Preview ref={previewRef} value={markdown} />
                            </Box>
                        </PreviewContainer>
                    </ContentContainer>

                    {/* Below-fold SEO content for crawlers and GEO */}
                    <Box
                        sx={{
                            bgcolor:
                                theme.palette.mode === "dark"
                                    ? "#0a1628"
                                    : "#f0f9ff",
                            borderTop: `1px solid ${
                                theme.palette.mode === "dark"
                                    ? "rgba(14, 165, 233, 0.1)"
                                    : "rgba(14, 165, 233, 0.15)"
                            }`,
                        }}
                    >
                    <SEOSection aria-label="About Markdown Converter Pro">

                        <h2>Free Online Markdown Converter</h2>
                        <p>
                            Markdown Converter Pro is a free, browser-based tool that lets you write Markdown and instantly export it as a PDF, Microsoft Word document (DOCX), or Google Doc. With a real-time preview, formatting toolbar, and dark mode support, it's the fastest way to turn your Markdown into professional documents.
                        </p>

                        <h3>How It Works</h3>
                        <p>
                            Write or paste your Markdown in the editor on the left. The preview panel on the right updates in real-time so you can see exactly how your document will look. When you're ready, click one of the export buttons to download as PDF, Word, or send directly to Google Docs.
                        </p>

                        <h3>Supported Markdown Features</h3>
                        <ul>
                            <li><strong>Headings</strong> (H1 through H6) for document structure</li>
                            <li><strong>Text formatting</strong>: bold, italic, strikethrough, inline code</li>
                            <li><strong>Lists</strong>: ordered, unordered, and task lists with checkboxes</li>
                            <li><strong>Tables</strong> with GitHub Flavored Markdown syntax</li>
                            <li><strong>Code blocks</strong> with syntax highlighting</li>
                            <li><strong>Blockquotes</strong> for callouts and citations</li>
                            <li><strong>Links and images</strong></li>
                            <li><strong>Horizontal rules</strong> for section breaks</li>
                        </ul>

                        <h3>Export Formats</h3>
                        <ul>
                            <li><strong>PDF</strong> - High-quality A4 documents, perfect for printing or sharing</li>
                            <li><strong>Word (DOCX)</strong> - Fully editable Microsoft Word documents with preserved formatting</li>
                            <li><strong>Google Docs</strong> - Create a new Google Doc directly from your Markdown content</li>
                            <li><strong>Rich Text Copy</strong> - Copy formatted text to paste into any application</li>
                        </ul>

                        <h2>Frequently Asked Questions</h2>
                        <FAQItem>
                            <summary>How do I convert Markdown to PDF?</summary>
                            <p>
                                Type or paste your Markdown in the editor, then click the green "PDF" button in the toolbar. Your document will be instantly generated as a professionally formatted A4 PDF and downloaded to your computer. No signup or installation needed.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>Can I convert Markdown to a Word document?</summary>
                            <p>
                                Yes! Click the purple "Word" button to export your Markdown as a Microsoft Word DOCX file. All formatting including headings, bold, italic, lists, tables, and code blocks are preserved in the Word document.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>How do I export Markdown to Google Docs?</summary>
                            <p>
                                Click the "Google Docs" button and sign in with your Google account. Markdown Converter Pro creates a new Google Doc with your formatted content, preserving all headings, lists, tables, and styling. The document opens automatically in a new tab.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>Is Markdown Converter Pro free?</summary>
                            <p>
                                Yes, completely free with no limitations. All conversions happen in your browser for maximum privacy and speed. No account required for PDF and Word exports.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>What is GitHub Flavored Markdown (GFM)?</summary>
                            <p>
                                GFM is an extension of standard Markdown used on GitHub. It adds support for tables, task lists, strikethrough text, and auto-linking. Markdown Converter Pro fully supports GFM syntax.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>Does the formatting toolbar support all Markdown features?</summary>
                            <p>
                                The toolbar provides quick access to the most common formatting options: headings, bold, italic, strikethrough, code, code blocks, bullet lists, numbered lists, task lists, links, blockquotes, horizontal rules, and tables. You can also type Markdown syntax directly.
                            </p>
                        </FAQItem>
                        <FAQItem>
                            <summary>Can I use this tool on mobile devices?</summary>
                            <p>
                                Yes, Markdown Converter Pro is fully responsive and works on phones and tablets. On smaller screens, the editor and preview panels stack vertically. You can also install it as a Progressive Web App (PWA) for offline access.
                            </p>
                        </FAQItem>
                    </SEOSection>
                    </Box>
                </main>
                <Auth
                    open={authOpen}
                    onClose={(accessToken) => {
                        setAuthOpen(false);
                        if (accessToken) {
                            setGoogleToken(accessToken);
                            handleGoogleDocs(accessToken);
                        }
                    }}
                />
                <Snackbar
                    open={copySuccess}
                    autoHideDuration={2000}
                    onClose={handleSnackbarClose}
                    message="Copied with formatting!"
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                />
            </Root>
        </ThemeProvider>
    );
}
